import { isFinanceProject, isGlobalProject } from 'config';
import { applySchema } from 'helpers';
import {
  TypedForm,
  FormErrors as FormErrorsBase,
  ObjectSchema,
  object,
} from 'libs/form';
import { TFunc } from 'libs/i18n';
import {
  AdditionalDocumentsFormType,
  getAdditionalDocumentsSchema,
} from 'modules/client/common/forms/additionalDocumentsForm';
import {
  DocumentsFormType,
  documentsSchema,
} from 'modules/client/common/forms/documentsForm';
import {
  ReasonChangeFormType,
  reasonChangeSchema,
} from 'modules/client/common/forms/reasonChangeForm';

import { AccountCodesFormType, accountCodesSchema } from './accountCodesForm';
import { BaseActivityFormType, baseActivitySchema } from './baseActivityForm';
import {
  CompanyActivitiesFormType,
  companyActivitiesSchema,
} from './companyActivitiesForm';
import {
  CompanyActivityFormType,
  getCompanyActivitySchema,
} from './companyActivityForm';
import {
  CompanyBankAccountsFormType,
  companyBankAccountsSchema,
} from './companyBankAccountsForm';
import {
  CompanyBusinessRelationshipFormType,
  getCompanyBusinessRelationshipSchema,
} from './companyBusinessRelationshipForm';
import {
  CompanyConfirmStructureFormType,
  companyConfirmStructureSchema,
} from './companyConfirmStructure';
import {
  CompanyConstituentDocsDateFormType,
  getCompanyConstituentDocsDateSchema,
} from './companyConstituentDocsDate';
import {
  CompanyConstituentDocsFilesFormType,
  getCompanyConstituentDocsFilesSchema,
} from './companyConstituentDocsFiles';
import {
  CompanyContactsFormType,
  getCompanyContactsSchema,
} from './companyContactsForm';
import { DebtsFormType, getDebtsSchema } from './companyDebtsForm';
import {
  CompanyFinanceFinancialMonitoringFormType,
  getCompanyFinanceFinancialMonitoringSchema,
} from './companyFinanceFinancialMonitoringForm';
import {
  CompanyFinancingSourcesFormType,
  getCompanyFinancingSourcesSchema,
} from './companyFinancingSourcesForm';
import {
  CompanyGlobalFinancialMonitoringFormType,
  getCompanyGlobalFinancialMonitoringSchema,
} from './companyGlobalFinancialMonitoringForm';
import {
  CompanyLicenseFormType,
  getCompanyLicenseSchema,
} from './companyLicenseForm';
import { CompanyNameFormType, companyNameSchema } from './companyNameForm';
import {
  CompanyOrderSubmissionContactsFormType,
  getCompanyOrderSubmissionContactsSchema,
} from './companyOrderSubmissionContactsForm';
import {
  CompanyOrganizationalAndLegalFormFormType,
  getCompanyOrganizationalAndLegalFormSchema,
} from './companyOrganizationalAndLegalFormForm';
import {
  CompanyRegistrationAddressFormType,
  getCompanyRegistrationAddressSchema,
} from './companyRegistrationAddressForm';
import {
  CompanyRegistrationPlaceFormType,
  companyRegistrationPlaceSchema,
} from './companyRegistrationPlaceForm';
import {
  CompanyResidentialAddressFormType,
  companyResidentialAddressSchema,
} from './companyResidentialAddressForm';
import {
  CompanyStateRegistrationFormType,
  getCompanyStateRegistrationSchema,
} from './companyStateRegistrationForm';
import {
  CompanyTaxResidencyFormType,
  companyTaxResidencySchema,
} from './companyTaxResidencyForm';
import {
  WithdrawalAccountsFormType,
  withdrawalAccountsSchema,
} from './companyWithdrawalAccountsForm';
import { GeneralFormType, generalSchema } from './generalForm';
import {
  getIdentificationNumbersSchema,
  IdentificationNumbersFormType,
} from './identificationNumbersForm';
import {
  getInvestmentPurposeAndNatureSchema,
  InvestmentPurposeAndNatureFormType,
} from './investmentPurposeAndNatureForm';
import {
  getKnowledgeAndExperienceSchema,
  KnowledgeAndExperienceFormType,
} from './knowledgeAndExperienceForm';
import {
  OpenAccountOrderFormType_Finance,
  openAccountOrderSchema_Finance,
} from './openAccountOrderForm';
import { RiskLevelFormType, riskLevelSchema } from './riskLevelForm';
import {
  ServiceContractFormType,
  serviceContractSchema,
} from './serviceContractForm';
import {
  SourcesOfIncomeFormType,
  getSourcesOfIncomeSchema,
} from './sourcesOfIncomeForm';
import { StatusesFormType, statusesSchema } from './statusesForm';
import {
  TargetedFinancialInstrumentsFormType,
  getTargetedFinancialInstrumentsSchema,
} from './targetedFinancialInstrumentsForm';
import { W8BenFormFormType, getW8BenFormSchema } from './w8BenFormForm';

export interface LegalClientFormType
  extends StatusesFormType,
    GeneralFormType,
    RiskLevelFormType,
    AccountCodesFormType,
    ServiceContractFormType,
    BaseActivityFormType,
    CompanyTaxResidencyFormType,
    CompanyStateRegistrationFormType,
    CompanyRegistrationPlaceFormType,
    CompanyNameFormType,
    CompanyLicenseFormType,
    CompanyFinancingSourcesFormType,
    CompanyFinanceFinancialMonitoringFormType,
    CompanyGlobalFinancialMonitoringFormType,
    CompanyContactsFormType,
    CompanyConstituentDocsFilesFormType,
    CompanyConstituentDocsDateFormType,
    CompanyConfirmStructureFormType,
    CompanyBusinessRelationshipFormType,
    CompanyActivitiesFormType,
    CompanyBankAccountsFormType,
    CompanyRegistrationAddressFormType,
    DebtsFormType,
    WithdrawalAccountsFormType,
    CompanyOrganizationalAndLegalFormFormType,
    CompanyOrderSubmissionContactsFormType,
    OpenAccountOrderFormType_Finance,
    CompanyResidentialAddressFormType,
    AdditionalDocumentsFormType,
    DocumentsFormType,
    TargetedFinancialInstrumentsFormType,
    ReasonChangeFormType,
    SourcesOfIncomeFormType,
    IdentificationNumbersFormType,
    CompanyActivityFormType,
    InvestmentPurposeAndNatureFormType,
    KnowledgeAndExperienceFormType,
    W8BenFormFormType {}

export const getSchema = (
  t: TFunc,
  hasBranches?: boolean
): ObjectSchema<LegalClientFormType> => {
  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();
  return object()
    .concat(statusesSchema)
    .concat(generalSchema)
    .concat(accountCodesSchema)
    .concat(serviceContractSchema)
    .concat(baseActivitySchema)
    .concat(riskLevelSchema)
    .concat(companyTaxResidencySchema)
    .concat(getCompanyRegistrationAddressSchema(isGlobal))
    .concat(companyResidentialAddressSchema)
    .concat(applySchema(isFinance, companyBankAccountsSchema))
    .concat(companyActivitiesSchema)
    .concat(companyConfirmStructureSchema)
    .concat(companyNameSchema)
    .concat(companyRegistrationPlaceSchema)
    .concat(getCompanyOrganizationalAndLegalFormSchema(isFinance))
    .concat(withdrawalAccountsSchema)
    .concat(applySchema(isFinance, openAccountOrderSchema_Finance))
    .concat(getCompanyLicenseSchema(t))
    .concat(getCompanyStateRegistrationSchema(t))
    .concat(applySchema(isFinance, getCompanyFinancingSourcesSchema(t)))
    .concat(
      applySchema(
        isFinance,
        getCompanyFinanceFinancialMonitoringSchema(t, hasBranches)
      )
    )
    .concat(applySchema(isGlobal, getCompanyGlobalFinancialMonitoringSchema(t)))
    .concat(getCompanyContactsSchema(t))
    .concat(getCompanyConstituentDocsFilesSchema())
    .concat(applySchema(isFinance, getCompanyConstituentDocsDateSchema(t)))
    .concat(getCompanyOrderSubmissionContactsSchema(t))
    .concat(applySchema(isFinance, getDebtsSchema(true)))
    .concat(applySchema(isFinance, getCompanyBusinessRelationshipSchema(t)))
    .concat(getAdditionalDocumentsSchema())
    .concat(applySchema(isGlobal, getTargetedFinancialInstrumentsSchema()))
    .concat(applySchema(isGlobal, getSourcesOfIncomeSchema(t)))
    .concat(documentsSchema)
    .concat(reasonChangeSchema)
    .concat(applySchema(isGlobal, getIdentificationNumbersSchema()))
    .concat(applySchema(isGlobal, getCompanyActivitySchema()))
    .concat(applySchema(isGlobal, getInvestmentPurposeAndNatureSchema(t)))
    .concat(applySchema(isGlobal, getW8BenFormSchema()))
    .concat(applySchema(isGlobal, getKnowledgeAndExperienceSchema()));
};

export const { Field, Form, useFormContext, Submit } =
  TypedForm<LegalClientFormType>();

export type FormErrors = FormErrorsBase<LegalClientFormType>;
